import type { ChatMessage, ChatPlaceholder, ChatRoomLock, ChatTemplateMessage, EMessageType } from '@if/core/types'
import { getApiClient } from '@src/services/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { useCallback } from 'react'

const CHAT_MESSAGES_URL = '/ChatMessages'
const LOCKS_URL = '/Chat/chatrooms'
const TEMPLATES_URL = '/ChatTemplateMessages'

// ── Messages ──

export interface ConversationResponse {
  conversationNumber: string
  fullName: string
  email: string
  phone: string
  province: string
  merchantName: string
  merchantId: string
  merchantLanguage: number
  applicationCreatedByName: string
  systemMessage: string
  applicationStatus: string
  messages: ChatMessage[]
}

const messageKeysFactory = {
  all: () => [{ scope: 'chat-messages' }] as const,
  conversation: (creditApplicationId: string, financingProgramId: string) =>
    [{ scope: 'chat-messages', creditApplicationId, financingProgramId }] as const,
}

async function getConversation(creditApplicationId: string, financingProgramId: string): Promise<ConversationResponse> {
  const apiClient = getApiClient()
  const response = await apiClient.get<ConversationResponse>(
    `${CHAT_MESSAGES_URL}/${creditApplicationId}?financingProgramId=${financingProgramId}`,
  )
  return response.data
}

export function useGetConversation(
  creditApplicationId: string | undefined,
  financingProgramId: string | undefined,
): [ConversationResponse | undefined, boolean, Error | null] {
  const { data, isLoading, error } = useQuery({
    queryKey: messageKeysFactory.conversation(creditApplicationId!, financingProgramId!),
    queryFn: () => getConversation(creditApplicationId!, financingProgramId!),
    enabled: !!creditApplicationId && !!financingProgramId,
    staleTime: 0,
    refetchOnMount: 'always',
  })
  return [data, isLoading, error]
}

export function useSendMessage() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async (message: {
      id: string
      content: string
      sentAt: Date
      sentByName: string
      sentById: string
      merchantId: string
      creditApplicationId: string
      financingProgramId: string
      isRead: boolean
      type: EMessageType
    }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<ChatMessage>(`${CHAT_MESSAGES_URL}/${message.creditApplicationId}`, message)
      return response.data
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: messageKeysFactory.all() })
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useEditMessage() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({
      creditApplicationId,
      messageId,
      content,
    }: {
      creditApplicationId: string
      messageId: string
      content: string
    }) => {
      const apiClient = getApiClient()
      await apiClient.put(`${CHAT_MESSAGES_URL}/${creditApplicationId}/${messageId}`, { newContent: content })
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useDeleteMessage() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId, messageId }: { creditApplicationId: string; messageId: string }) => {
      const apiClient = getApiClient()
      await apiClient.delete(`${CHAT_MESSAGES_URL}/${creditApplicationId}/${messageId}`)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useMarkMessageRead() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId, messageId }: { creditApplicationId: string; messageId: string }) => {
      const apiClient = getApiClient()
      await apiClient.post(`${CHAT_MESSAGES_URL}/${creditApplicationId}/${messageId}/MarkAsRead`)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useMarkMessageUnread() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId, messageId }: { creditApplicationId: string; messageId: string }) => {
      const apiClient = getApiClient()
      await apiClient.post(`${CHAT_MESSAGES_URL}/${creditApplicationId}/${messageId}/MarkAsUnread`)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useMarkConversationAsRead() {
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({ creditApplicationId }: { creditApplicationId: string }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<{ success: boolean; messageCount: number }>(
        `${CHAT_MESSAGES_URL}/${creditApplicationId}/MarkAsRead`,
      )
      return response.data
    },
  })
  return [mutateAsync, isPending, error] as const
}

export { messageKeysFactory as chatMessageKeysFactory }

// ── Locks ──

const lockKeysFactory = {
  all: () => [{ scope: 'chatroom-lock' }] as const,
  detail: (creditApplicationId: string) => [{ scope: 'chatroom-lock', creditApplicationId }] as const,
}

export function useGetChatRoomLock(
  creditApplicationId: string | undefined,
): [ChatRoomLock | undefined, boolean, Error | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: lockKeysFactory.detail(creditApplicationId!),
    queryFn: async () => {
      const apiClient = getApiClient()
      const response = await apiClient.get<ChatRoomLock>(`${LOCKS_URL}/${creditApplicationId}/lock`)
      return response.data
    },
    enabled: !!creditApplicationId,
  })
  return [data, isFetching, error]
}

export function useAcquireChatRoomLock() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({
      creditApplicationId,
      merchantId,
      financingProgramId,
    }: {
      creditApplicationId: string
      merchantId: string
      financingProgramId: string
    }) => {
      const apiClient = getApiClient()
      const response = await apiClient.post<ChatRoomLock>(`${LOCKS_URL}/${creditApplicationId}/lock`, {
        merchantId,
        financingProgramId,
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(lockKeysFactory.detail(data.creditApplicationId), data)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function releaseChatRoomLockOnUnload(creditApplicationId: string) {
  const baseURL = import.meta.env.VITE_BACKOFFICE_API_URL
  const url = `${baseURL}${LOCKS_URL}/${creditApplicationId}/lock`
  const apiClient = getApiClient()
  const authHeader = apiClient.defaults.headers.Authorization as string | undefined
  void fetch(url, {
    method: 'DELETE',
    keepalive: true,
    headers: {
      ...(authHeader ? { Authorization: authHeader } : {}),
    },
  })
}

export function useReleaseChatRoomLock() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async (creditApplicationId: string) => {
      const apiClient = getApiClient()
      const response = await apiClient.delete<ChatRoomLock>(`${LOCKS_URL}/${creditApplicationId}/lock`)
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(lockKeysFactory.detail(data.creditApplicationId), data)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useForceAcquireChatRoomLock() {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: async ({
      creditApplicationId,
      merchantId,
      financingProgramId,
    }: {
      creditApplicationId: string
      merchantId: string
      financingProgramId: string
    }) => {
      const apiClient = getApiClient()
      const response = await apiClient.put<ChatRoomLock>(`${LOCKS_URL}/${creditApplicationId}/lock`, {
        merchantId,
        financingProgramId,
      })
      return response.data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(lockKeysFactory.detail(data.creditApplicationId), data)
    },
  })
  return [mutateAsync, isPending, error] as const
}

export function useUpdateLockCache() {
  const queryClient = useQueryClient()
  return useCallback(
    (lock: ChatRoomLock) => {
      queryClient.setQueryData(lockKeysFactory.detail(lock.creditApplicationId), lock)
    },
    [queryClient],
  )
}

export { lockKeysFactory as chatLockKeysFactory }

// ── Templates ──

const templateKeysFactory = {
  all: () => [{ scope: 'chat-templates' }] as const,
  list: (creditApplicationId: string, financingProgramId: string) =>
    [{ scope: 'chat-templates', creditApplicationId, financingProgramId }] as const,
  detail: (code: string, creditApplicationId: string, financingProgramId: string) =>
    [{ scope: 'chat-templates', entity: 'detail', code, creditApplicationId, financingProgramId }] as const,
}

export function useGetTemplates(
  creditApplicationId: string | undefined,
  financingProgramId: string | undefined,
): [ChatTemplateMessage[] | undefined, boolean, Error | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: templateKeysFactory.list(creditApplicationId!, financingProgramId!),
    queryFn: async () => {
      try {
        const apiClient = getApiClient()
        const response = await apiClient.get<ChatTemplateMessage[]>(
          `${TEMPLATES_URL}/${financingProgramId}/${creditApplicationId}`,
        )
        return response.data || []
      } catch (err) {
        const axiosError = err as AxiosError
        if (axiosError.response?.status === 403) return []
        throw err
      }
    },
    enabled: !!creditApplicationId && !!financingProgramId,
    staleTime: 5 * 60 * 1000,
    refetchOnMount: true,
  })
  return [data, isFetching, error]
}

export function useGetTemplate(
  code: string | undefined,
  creditApplicationId: string | undefined,
  financingProgramId: string | undefined,
): [ChatTemplateMessage | null | undefined, boolean, Error | null] {
  const { data, isFetching, error } = useQuery({
    queryKey: templateKeysFactory.detail(code!, creditApplicationId!, financingProgramId!),
    queryFn: async () => {
      try {
        const apiClient = getApiClient()
        const response = await apiClient.get<ChatTemplateMessage>(
          `${TEMPLATES_URL}/${encodeURIComponent(financingProgramId!)}/${encodeURIComponent(creditApplicationId!)}/${encodeURIComponent(code!)}`,
        )
        return response.data
      } catch (err) {
        const axiosError = err as AxiosError
        if (axiosError.response?.status === 404 || axiosError.response?.status === 403) return null
        throw err
      }
    },
    enabled: !!code && !!creditApplicationId && !!financingProgramId,
    staleTime: 0,
    gcTime: 0,
  })
  return [data, isFetching, error]
}

export { templateKeysFactory as chatTemplateKeysFactory }

const placeholderKeysFactory = {
  detail: (creditApplicationId: string, financingProgramId: string) =>
    [{ scope: 'chat-placeholders', creditApplicationId, financingProgramId }] as const,
}

export function useGetPlaceholders(
  creditApplicationId: string | undefined,
  financingProgramId: string | undefined,
): [ChatPlaceholder[], boolean] {
  const { data, isFetching } = useQuery({
    queryKey: placeholderKeysFactory.detail(creditApplicationId!, financingProgramId!),
    queryFn: async () => {
      const apiClient = getApiClient()
      const response = await apiClient.get<ChatPlaceholder[]>(
        `${TEMPLATES_URL}/placeholders/${financingProgramId}/${creditApplicationId}`,
      )
      return response.data
    },
    enabled: !!creditApplicationId && !!financingProgramId,
    staleTime: 5 * 60 * 1000,
  })
  return [data ?? [], isFetching]
}
